import { Fragment, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useStartProject } from "@/lib/startProjectContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { detectLocale, stripLocale, withLocale } from "@/lib/i18n";
import { NAV_PRODUCT_CATEGORIES, NAV_SOURCING_SERVICES } from "@/data/navCatalogue";

/**
 * Sprint 30 · Nav redesign.
 * - Desktop: two mega-menu dropdowns ("Product Categories", "Sourcing Services"),
 *   each with a clickable PARENT link to the top-level landing page plus the
 *   full catalogue of children. Zero new routes beyond the 5 Sprint-30 hubs.
 * - Mobile: full-screen overlay with accessible accordions — each parent is a
 *   clickable link AND an expand/collapse control.
 * - Every link maps to an existing indexed URL (no 404s, no broken anchors).
 */
const NAV_ITEMS = [
  { to: "/trade-fairs", label: "Trade fairs", section: "/trade-fairs" },
  { to: "/industries", label: "Industries", section: "/industries" },
  { to: "/about", label: "About", section: "/about" },
  { to: "/trends", label: "Insights", section: "/trends" },
];

const DARK_HERO_ROUTES = new Set(["/", "/industries", "/start-a-project"]);

const MEGA_SECTIONS = [
  { id: "products",  label: "Product Categories",  parent: "/categories",   items: NAV_PRODUCT_CATEGORIES },
  { id: "services",  label: "Sourcing Services",   parent: "/services",     items: NAV_SOURCING_SERVICES },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState({ products: false, services: false });
  const location = useLocation();
  const { open: openProject } = useStartProject();

  const canonicalPath = stripLocale(location.pathname);
  const locale = detectLocale(location.pathname);
  const hasDarkHero = DARK_HERO_ROUTES.has(canonicalPath);
  const navSolid = !hasDarkHero || scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.pageYOffset > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileOpen({ products: false, services: false });
  }, [location.pathname]);

  const isActive = (item) => {
    if (item.section === "/industries") return canonicalPath.startsWith("/industries");
    if (item.section === "/trends") return canonicalPath.startsWith("/trends") || canonicalPath.startsWith("/news");
    return canonicalPath.startsWith(item.section);
  };
  const sectionActive = (parent) => canonicalPath.startsWith(parent);

  return (
    <>
      <header className={`qh-nav ${navSolid ? "scrolled" : ""}`} data-testid="site-nav">
        <div className="qh-nav-inner">
          <Link to={withLocale("/", locale)} className="qh-brand" data-testid="brand-link" aria-label="Asia Sourcing — home">
            <img src="/brand/asia-sourcing-logo-light.png" alt="Asia Sourcing" className="qh-brand-logo" width="1502" height="560" />
          </Link>

          <nav className="qh-links" aria-label="Primary">
            {MEGA_SECTIONS.map((sec) => (
              <div key={sec.id} className="qh-dropdown" data-testid={`nav-${sec.id}`}>
                <Link
                  to={withLocale(sec.parent, locale)}
                  className={`qh-link qh-dropdown-toggle ${sectionActive(sec.parent) ? "active" : ""}`}
                  data-testid={`nav-${sec.id}-link`}
                  aria-haspopup="true"
                >
                  {sec.label} <ChevronDown className="qh-caret" size={12} strokeWidth={1.6} aria-hidden="true" />
                </Link>
                <div className="qh-mega" data-testid={`nav-${sec.id}-panel`} role="menu" aria-label={sec.label}>
                  <div className="qh-mega-inner" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
                    {sec.items.map((it) => (
                      <Link
                        key={it.to}
                        to={withLocale(it.to, locale)}
                        className="qh-mega-link"
                        data-testid={`nav-${sec.id}-${it.to.split("/").pop()}`}
                        role="menuitem"
                      >
                        {it.label}
                      </Link>
                    ))}
                  </div>
                  <Link to={withLocale(sec.parent, locale)} className="qh-mega-all" data-testid={`nav-${sec.id}-all`}>
                    View all {sec.label.toLowerCase()} →
                  </Link>
                </div>
              </div>
            ))}
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={withLocale(item.to, locale)}
                className={() => `qh-link ${isActive(item) ? "active" : ""}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <LanguageSwitcher />

          <button type="button" className="qh-cta qh-cta-desktop" onClick={() => { openProject(); }} data-testid="nav-start-project">
            Start Your Project
          </button>

          <button
            type="button"
            className="qh-burger"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-burger"
          >
            {open ? <X size={22} strokeWidth={1.4} /> : <Menu size={22} strokeWidth={1.4} />}
          </button>
        </div>
      </header>

      <nav className={`qh-mobile-menu ${open ? "open" : ""}`} aria-label="Mobile menu" data-testid="mobile-menu">
        {MEGA_SECTIONS.map((sec) => (
          <Fragment key={`m-${sec.id}`}>
            <div className="qh-mobile-section" data-testid={`mobile-nav-${sec.id}`}>
              <div className="qh-mobile-section-row">
                <Link
                  to={withLocale(sec.parent, locale)}
                  className={`qh-link ${sectionActive(sec.parent) ? "active" : ""}`}
                  data-testid={`mobile-nav-${sec.id}-parent`}
                  onClick={() => setOpen(false)}
                >
                  {sec.label}
                </Link>
                <button
                  type="button"
                  className="qh-mobile-decor-toggle"
                  onClick={() => setMobileOpen((v) => ({ ...v, [sec.id]: !v[sec.id] }))}
                  aria-expanded={mobileOpen[sec.id]}
                  aria-label={`${mobileOpen[sec.id] ? "Collapse" : "Expand"} ${sec.label}`}
                  data-testid={`mobile-nav-${sec.id}-toggle`}
                >
                  <ChevronDown className="qh-caret" size={18} strokeWidth={1.4} aria-hidden="true" style={{ transform: mobileOpen[sec.id] ? "rotate(180deg)" : "none" }} />
                </button>
              </div>
              {mobileOpen[sec.id] && (
                <div className="qh-mobile-decor-panel" data-testid={`mobile-nav-${sec.id}-panel`}>
                  {sec.items.map((it) => (
                    <Link
                      key={`m-${it.to}`}
                      to={withLocale(it.to, locale)}
                      className="qh-mobile-decor-link"
                      data-testid={`mobile-nav-${sec.id}-${it.to.split("/").pop()}`}
                      onClick={() => setOpen(false)}
                    >
                      {it.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </Fragment>
        ))}
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={`m-${item.to}`}
            to={withLocale(item.to, locale)}
            className={() => `qh-link ${isActive(item) ? "active" : ""}`}
            data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setOpen(false)}
          >
            {item.label}
          </NavLink>
        ))}
        <button type="button" className="qh-cta" onClick={() => { openProject(); setOpen(false); }} data-testid="mobile-nav-start-project">
          Start Your Project →
        </button>
        <div style={{ marginTop: "1.4rem", display: "flex", justifyContent: "center" }} data-testid="mobile-language-switcher">
          <LanguageSwitcher dropUp />
        </div>
      </nav>
    </>
  );
};

export default Nav;
